import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Anchor, 
  User, 
  Calendar, 
  MapPin, 
  Clock, 
  Ship, 
  Navigation,
  LogOut,
  Plus,
  Receipt,
  Loader2,
  ChevronRight,
  Car,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "@/config/api";
import { usePushNotifications } from "@/components/PushNotifications";

// Status badge colors
const statusColors = {
  pending: "bg-amber-100 text-amber-800",
  assigned: "bg-blue-100 text-blue-800",
  in_progress: "bg-purple-100 text-purple-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800"
};

const paymentStatusColors = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-green-100 text-green-800",
  paid_by_cash: "bg-emerald-100 text-emerald-800"
};

export default function ClientDashboardPage() {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBooking, setActiveBooking] = useState(null);
  const { showLocalNotification, permission } = usePushNotifications();

  // Check authentication and fetch data
  useEffect(() => {
    const token = localStorage.getItem("client_token");
    const clientInfo = localStorage.getItem("client_info");
    
    if (!token) {
      navigate("/client/login");
      return;
    }
    
    if (clientInfo) {
      setClient(JSON.parse(clientInfo));
    }
    
    fetchBookings(token);
    checkGeofence(token);
  }, [navigate]);

  const fetchBookings = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/api/client/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allBookings = response.data.bookings || [];
      setBookings(allBookings);
      
      // Find active booking (assigned or in_progress)
      const active = allBookings.find(b => 
        b.status === "assigned" || b.status === "in_progress"
      );
      setActiveBooking(active);
      
    } catch (error) {
      console.error("Failed to fetch bookings:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("client_token");
        localStorage.removeItem("client_info");
        navigate("/client/login");
      }
    } finally {
      setLoading(false);
    }
  };

  // Check geofence on page load (Option A)
  const checkGeofence = async (token) => {
    if (!navigator.geolocation || permission !== 'granted') return;
    
    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        const response = await axios.post(
          `${API_URL}/api/client/check-geofence?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.should_notify) {
          // Show geofence notification
          showLocalNotification("Welcome to " + response.data.terminal_name + "!", {
            body: "Need a shuttle to the city? Book your Y GO ride now!",
            tag: "geofence-welcome",
            requireInteraction: true,
            data: { url: "/booking" }
          });
          
          toast.info(`Welcome near ${response.data.terminal_name}!`, {
            description: "Need a shuttle? Book your ride now!",
            action: {
              label: "Book Now",
              onClick: () => navigate("/booking")
            },
            duration: 10000
          });
        }
      } catch (error) {
        console.log("Geofence check skipped:", error.message);
      }
    }, (error) => {
      console.log("Geolocation unavailable:", error.message);
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("client_token");
    localStorage.removeItem("client_info");
    toast.success("Logged out successfully");
    navigate("/");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Header */}
      <header className="bg-slate-900/50 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-md bg-orange-500 flex items-center justify-center">
                <Anchor className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-black text-white tracking-tight">Y GO</span>
            </Link>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 text-slate-400">
                <User className="w-4 h-4" />
                <span className="text-sm">{client?.name}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">
            Hello, {client?.name?.split(' ')[0] || 'there'}! 👋
          </h1>
          <p className="text-slate-400">Manage your bookings and track your rides</p>
        </div>

        {/* Active Booking Card */}
        {activeBooking && (
          <Card className="mb-6 border-2 border-orange-500 bg-gradient-to-r from-orange-50 to-amber-50 shadow-lg">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Active Booking
                </CardTitle>
                <Badge className={statusColors[activeBooking.status]}>
                  {activeBooking.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                <div className="flex items-center gap-3">
                  <Ship className="w-5 h-5 text-orange-500" />
                  <span className="font-medium">{activeBooking.ship_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-orange-500" />
                  <span>{activeBooking.terminal_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-orange-500" />
                  <span>{formatDate(activeBooking.pickup_datetime)} at {formatTime(activeBooking.pickup_datetime)}</span>
                </div>
                {activeBooking.assigned_driver_name && (
                  <div className="flex items-center gap-3">
                    <Car className="w-5 h-5 text-orange-500" />
                    <span>Driver: <strong>{activeBooking.assigned_driver_name}</strong></span>
                  </div>
                )}
              </div>
              
              <div className="mt-4 flex gap-3">
                <Link to={`/track/${activeBooking.tracking_token || activeBooking.booking_id}`} className="flex-1">
                  <Button className="w-full bg-orange-500 hover:bg-orange-600">
                    <Navigation className="w-4 h-4 mr-2" />
                    Track Driver
                  </Button>
                </Link>
                <Link to={`/client/booking/${activeBooking.booking_id}`}>
                  <Button variant="outline">
                    <Receipt className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Link to="/booking">
            <Card className="hover:bg-slate-50 transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-3">
                  <Plus className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-semibold text-slate-900">New Booking</h3>
                <p className="text-sm text-slate-500">Book a shuttle</p>
              </CardContent>
            </Card>
          </Link>
          
          <button onClick={() => fetchBookings(localStorage.getItem("client_token"))}>
            <Card className="hover:bg-slate-50 transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                  <RefreshCw className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900">Refresh</h3>
                <p className="text-sm text-slate-500">Update status</p>
              </CardContent>
            </Card>
          </button>
        </div>

        {/* Booking History */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg">Booking History</CardTitle>
          </CardHeader>
          <CardContent>
            {bookings.length === 0 ? (
              <div className="text-center py-12">
                <Ship className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No bookings yet</p>
                <Link to="/booking">
                  <Button className="bg-orange-500 hover:bg-orange-600">
                    Book Your First Shuttle
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {bookings.map((booking) => (
                  <Link 
                    key={booking.booking_id} 
                    to={`/client/booking/${booking.booking_id}`}
                    className="block"
                  >
                    <div className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm font-bold text-orange-600">
                              {booking.booking_id}
                            </span>
                            <Badge className={statusColors[booking.status] || "bg-slate-100"}>
                              {booking.status}
                            </Badge>
                          </div>
                          <p className="font-medium text-slate-900 truncate">
                            {booking.ship_name}
                          </p>
                          <p className="text-sm text-slate-500">
                            {booking.terminal_name} • {formatDate(booking.pickup_datetime)}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge className={paymentStatusColors[booking.payment_status] || "bg-slate-100"}>
                              {booking.payment_status === "paid" ? (
                                <><CheckCircle2 className="w-3 h-3 mr-1" /> Paid</>
                              ) : booking.payment_status === "paid_by_cash" ? (
                                <><CheckCircle2 className="w-3 h-3 mr-1" /> Paid Cash</>
                              ) : (
                                <><CreditCard className="w-3 h-3 mr-1" /> €{booking.final_price?.toFixed(2)}</>
                              )}
                            </Badge>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-slate-500 text-sm">
          <p>Need help? Contact us at +33 2 78 78 04 04</p>
        </div>
      </div>
    </div>
  );
}
